import { type FormEvent, useEffect, useState } from "react";

import { InnerPageChrome } from "@/components/layout/InnerPageChrome";
import { MINIMALISTIC_PORTFOLIO_STYLESHEETS } from "@/pages/shared/vorinDemoStyles";
import { useVorinAssets } from "@/pages/shared/useVorinAssets";
import type { CmsContentPageData, CmsParagraph } from "@/pages/shared/cmsContent";
import { cmsMediaUrl, useCmsContentPage } from "@/pages/shared/cmsContent";
import { getSiteLocale, localizeHref } from "@/i18n/locale";
import { localizeContent, translateText } from "@/i18n/translations";

import styles from "./EditorialPages.module.scss";

type RouteLink = {
  href: string;
  label: string;
  meta: string;
};

type CmsManagedPageProps = {
  slug: string;
  currentPath: string;
  eyebrow: string;
  visualImage: string;
  visualAlt: string;
  leadTitle: string;
  leadCopy: string;
  routeLinks: readonly RouteLink[];
  primaryCtaLabel: string;
  primaryCtaHref: string;
  secondaryCtaLabel: string;
  secondaryCtaHref: string;
  privacyPolicyHref?: string;
  contactForm?: boolean;
  minimalContent?: boolean;
  hideHeroIntro?: boolean;
  hideConnectedIntro?: boolean;
  connectedTitle?: string;
  connectedCopy?: string;
  servicePanelTitle?: string;
  servicePanelNote?: string;
  servicePanelBody?: string;
  servicePanelAccent?: string;
  hideServiceNumber?: boolean;
  hideServiceLabel?: boolean;
  visualFit?: "cover" | "contain";
  featureTitle?: string;
  featureCopy?: string;
  featureParagraphs?: readonly CmsParagraph[];
  hideFeatureIndex?: boolean;
  formTitle?: string;
  formIntro?: string;
  formSubmitLabel?: string;
  formSuccessMessage?: string;
  fallback: CmsContentPageData;
  transformPage?: (page: CmsContentPageData) => CmsContentPageData;
};

export function CmsManagedPage({
  slug,
  currentPath,
  eyebrow,
  visualImage,
  visualAlt,
  leadTitle,
  leadCopy,
  routeLinks,
  primaryCtaLabel,
  primaryCtaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  privacyPolicyHref,
  contactForm = false,
  minimalContent = false,
  hideHeroIntro = false,
  hideConnectedIntro = false,
  connectedTitle = "Connected routes",
  connectedCopy,
  servicePanelTitle,
  servicePanelNote,
  servicePanelBody,
  servicePanelAccent,
  hideServiceNumber = false,
  hideServiceLabel = false,
  visualFit = "cover",
  featureTitle = "Core content",
  featureCopy,
  featureParagraphs,
  hideFeatureIndex = false,
  formTitle = "START YOUR PROJECT",
  formIntro = "Share the essentials below so we can understand your project and prepare for a meaningful first conversation.",
  formSubmitLabel = "START THE CONVERSATION",
  formSuccessMessage = "Thank you. Your enquiry has been received. We’ll be in touch shortly to discuss the next steps.",
  fallback,
  transformPage,
}: CmsManagedPageProps) {
  useVorinAssets({ extraStylesheets: MINIMALISTIC_PORTFOLIO_STYLESHEETS });
  const locale = getSiteLocale();
  const translate = (value: string | undefined) => value ? translateText(value, locale) : value;

  const sourcePage = useCmsContentPage(slug, fallback);
  const page = transformPage ? transformPage(sourcePage) : sourcePage;
  const presentation = page.presentation;
  const resolvedEyebrow = translate(presentation?.eyebrow ?? eyebrow);
  const resolvedVisualImage = cmsMediaUrl(presentation?.visual, visualImage);
  const resolvedVisualAlt = translate(presentation?.visual?.alt ?? visualAlt);
  const resolvedVisualPosition = presentation?.visual?.position || "center center";
  const resolvedVisualFit = presentation?.visual_fit ?? visualFit;
  const resolvedLeadTitle = translate(presentation?.lead_title ?? leadTitle);
  const resolvedLeadCopy = translate(presentation?.lead_copy ?? leadCopy);
  const resolvedRouteLinks = localizeContent(presentation?.route_links ?? routeLinks, locale);
  const resolvedPrimaryLink = localizeContent(presentation?.primary_link ?? {
    label: primaryCtaLabel,
    href: primaryCtaHref,
  }, locale);
  const resolvedSecondaryLink = localizeContent(presentation?.secondary_link ?? {
    label: secondaryCtaLabel,
    href: secondaryCtaHref,
  }, locale);
  const resolvedPrivacyHref = localizeHref(presentation?.privacy_policy_href ?? privacyPolicyHref ?? "", locale);
  const resolvedContactForm = presentation?.show_contact_form ?? contactForm;
  const resolvedMinimalContent = presentation?.minimal_content ?? minimalContent;
  const resolvedHideHeroIntro = presentation?.hide_hero_intro ?? hideHeroIntro;
  const resolvedHideConnectedIntro = presentation?.hide_connected_intro ?? hideConnectedIntro;
  const resolvedConnectedTitle = translate(presentation?.connected_title ?? connectedTitle);
  const resolvedConnectedCopy = translate(presentation?.connected_copy ?? connectedCopy);
  const resolvedPanelTitle = translate(presentation?.panel_title ?? servicePanelTitle);
  const resolvedPanelNote = translate(presentation?.panel_note ?? servicePanelNote);
  const resolvedPanelBody = translate(presentation?.panel_body ?? servicePanelBody);
  const resolvedPanelAccent = translate(presentation?.panel_accent ?? servicePanelAccent);
  const resolvedHidePanelNumber = presentation?.hide_panel_number ?? hideServiceNumber;
  const resolvedHidePanelLabel = presentation?.hide_panel_label ?? hideServiceLabel;
  const resolvedFeatureTitle = translate(presentation?.feature_title ?? featureTitle);
  const resolvedFeatureCopy = translate(presentation?.feature_copy ?? featureCopy);
  const resolvedHideFeatureIndex = presentation?.hide_feature_index ?? hideFeatureIndex;
  const resolvedFeatures = presentation?.features;
  const resolvedForm = localizeContent(presentation?.contact_form, locale);
  const resolvedFormTitle = translate(resolvedForm?.title ?? formTitle);
  const resolvedFormIntro = translate(resolvedForm?.intro ?? formIntro);
  const resolvedFormSubmitLabel = translate(resolvedForm?.submit_label ?? formSubmitLabel);
  const resolvedFormSubmittingLabel = translate(resolvedForm?.submitting_label ?? "Sending...");
  const resolvedFormSuccessMessage = translate(resolvedForm?.success_message ?? formSuccessMessage);
  const hasSections = page.sections.length > 0;
  const leadParagraph = page.paragraphs[0] ?? null;
  const narrativeParagraphs = page.paragraphs.slice(1);
  const cards = localizeContent(featureParagraphs ?? (narrativeParagraphs.length > 0 ? narrativeParagraphs : page.paragraphs), locale);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const fallbackMailto = resolvedPrimaryLink.href.replace(/^mailto:/, "");

  useEffect(() => {
    document.title = `${page.seoTitle || page.title} | VenueX`;
    const descriptionTag = document.querySelector('meta[name="description"]');
    descriptionTag?.setAttribute("content", page.searchDescription || page.intro);
  }, [page.intro, page.searchDescription, page.seoTitle, page.title]);

  async function handleContactSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (formSubmitting) return;

    const formData = new FormData(event.currentTarget);
    const payload = {
      name: String(formData.get("name") || ""),
      company: String(formData.get("company") || ""),
      email: String(formData.get("email") || ""),
      phone: String(formData.get("phone") || ""),
      projectType: String(formData.get("projectType") || ""),
      location: String(formData.get("location") || ""),
      timeframe: String(formData.get("timeframe") || ""),
      budget: String(formData.get("budget") || ""),
      referral: String(formData.get("referral") || ""),
      message: String(formData.get("message") || ""),
      language: locale,
      // Honeypot -- left empty by real visitors, filled in by bots that
      // blindly fill every field they find.
      website: String(formData.get("website") || ""),
    };

    setFormSubmitting(true);
    setFormError("");

    try {
      const response = await fetch("/api/contact/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({ ok: false }));

      if (!response.ok || !result.ok) {
        throw new Error(result.error || (locale === "el" ? "Δεν ήταν δυνατή η αποστολή. Παρακαλούμε δοκιμάστε ξανά." : "Couldn't send your message. Please try again."));
      }

      setFormSubmitted(true);
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : locale === "el"
            ? `Δεν ήταν δυνατή η αποστολή. Στείλτε μας email στο ${fallbackMailto}.`
            : `Couldn't send your message. Please email us directly at ${fallbackMailto}.`,
      );
    } finally {
      setFormSubmitting(false);
    }
  }

  return (
    <InnerPageChrome currentPath={currentPath}>
      <article className={styles.page}>
        <section className={styles.hero}>
          <div className="container-fluid">
            <div className={styles.heroGrid}>
              <div>
                <span className={styles.eyebrow}>{resolvedEyebrow}</span>
                <h1 className={styles.heroTitle}>{page.title}</h1>
              </div>

              {(!resolvedHideHeroIntro || hasSections) && <p className={styles.heroLead}>{page.intro}</p>}
            </div>
          </div>
        </section>

        {page.sections.map((section, index) =>
          section.type === "profile" ? (
            <section key={`${page.slug}-section-${index}`} className={styles.section}>
              <div className="container-fluid">
                <h2 className={`${styles.sectionTitle} ${styles.profileHeading}`}>{section.heading}</h2>
                <div className={`${styles.profile} ${section.photoUrl ? "" : styles.profileNoPhoto}`.trim()}>
                  {section.photoUrl && (
                    <img className={styles.profilePhoto} src={section.photoUrl} alt={section.photoAlt} />
                  )}
                  <div className={styles.profileText}>
                    <h3 className={styles.profileName}>{section.name}</h3>
                    {section.role && <p className={styles.profileRole}>{section.role}</p>}
                    <div
                      className={styles.richParagraph}
                      dangerouslySetInnerHTML={{ __html: section.bodyHtml }}
                    />
                    {section.buttonLabel && section.buttonUrl && (
                      <a
                        className={styles.profileButton}
                        href={section.buttonUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {section.buttonLabel}
                        <span aria-hidden="true">&#8594;</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <section key={`${page.slug}-section-${index}`} className={styles.section}>
              <div className="container-fluid">
                <div className={styles.textSection}>
                  <h2 className={styles.sectionTitle}>{section.heading}</h2>
                  <div
                    className={`${styles.richParagraph} ${styles.textSectionBody}`}
                    dangerouslySetInnerHTML={{ __html: section.bodyHtml }}
                  />
                </div>
              </div>
            </section>
          ),
        )}

        {!hasSections && <section className={styles.section}>
          <div className="container-fluid">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{resolvedLeadTitle}</h2>
              <p className={styles.sectionCopy}>{resolvedLeadCopy}</p>
            </div>

            <div className={styles.serviceCard}>
              <div
                className={`${styles.serviceVisual} ${resolvedVisualFit === "contain" ? styles.serviceVisualContain : ""}`.trim()}
              >
                {!resolvedHidePanelLabel && <span className={styles.serviceLabel}>{page.seoTitle}</span>}
                <img
                  src={resolvedVisualImage}
                  alt={resolvedVisualAlt}
                  style={{ objectPosition: resolvedVisualPosition }}
                />
              </div>

              <div className={styles.servicePanel}>
                {!resolvedHidePanelNumber && (
                  <div className={styles.serviceNumber}>
                    {String(page.id).padStart(2, "0")}
                  </div>
                )}
                <h3 className={styles.serviceTitle}>{resolvedPanelTitle ?? page.title}</h3>
                {(!resolvedMinimalContent || resolvedPanelTitle || resolvedPanelBody || resolvedPanelAccent) && (
                  <>
                    {(resolvedPanelNote ?? page.searchDescription ?? page.intro) && (
                      <p className={styles.serviceNote}>
                        {resolvedPanelNote ?? page.searchDescription ?? page.intro}
                      </p>
                    )}
                    {resolvedPanelBody ? (
                      <p className={styles.serviceBody}>{resolvedPanelBody}</p>
                    ) : leadParagraph ? (
                      <div
                        className={styles.richParagraph}
                        dangerouslySetInnerHTML={{ __html: leadParagraph.html }}
                      />
                    ) : (
                      <p className={styles.serviceBody}>{page.intro}</p>
                    )}
                    <span className={styles.serviceAccent}>
                      {resolvedPanelAccent ?? (locale === "el" ? "Με την άμεση επιμέλεια του studio της VenueX." : "Managed directly from the VenueX studio.")}
                    </span>
                  </>
                )}

              </div>
            </div>
          </div>
        </section>}

        {resolvedContactForm && (
          <section className={styles.contactFormSection}>
            <div className="container-fluid">
              {formSubmitted ? (
                <div className={styles.formSuccess} role="status">
                  {resolvedFormSuccessMessage}
                </div>
              ) : (
                <div className={styles.contactFormLayout}>
                  <div className={styles.contactFormHeader}>
                    <h2 className={styles.sectionTitle}>{resolvedFormTitle}</h2>
                    <p className={styles.sectionCopy}>{resolvedFormIntro}</p>
                  </div>

                  <form className={`${styles.contactForm} ${styles.contactFormLight}`} onSubmit={handleContactSubmit}>
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                    />
                    <div className={styles.formRow}>
                      <label className={styles.formField}>
                        <span>{resolvedForm?.name_label ?? translateText("Full Name*", locale)}</span>
                        <input name="name" type="text" autoComplete="name" required />
                      </label>
                      <label className={styles.formField}>
                        <span>{resolvedForm?.company_label ?? translateText("Company / Brand", locale)}</span>
                        <input name="company" type="text" autoComplete="organization" />
                      </label>
                    </div>

                    <div className={styles.formRow}>
                      <label className={styles.formField}>
                        <span>{resolvedForm?.email_label ?? translateText("Email Address*", locale)}</span>
                        <input name="email" type="email" autoComplete="email" required />
                      </label>
                      <label className={styles.formField}>
                        <span>{resolvedForm?.phone_label ?? translateText("Phone Number", locale)}</span>
                        <input name="phone" type="tel" autoComplete="tel" />
                      </label>
                    </div>

                    <label className={styles.formField}>
                      <span>{resolvedForm?.service_label ?? translateText("Service You're Interested In*", locale)}</span>
                      <select name="projectType" defaultValue="" required>
                        <option value="" disabled>
                          {resolvedForm?.service_placeholder ?? translateText("Select one", locale)}
                        </option>
                        {(resolvedForm?.service_options ?? [
                          "Brand Strategy & Identity",
                          "Websites & Digital Solutions",
                          "Digital Marketing & Social Media",
                          "Corporate Events & Brand Activations",
                          "Conferences & Exhibitions",
                          "Wedding Consulting & Destination Weddings",
                          "Multiple Services",
                          "Not Sure Yet",
                        ].map((option) => translateText(option, locale))).map((option) => (
                          <option key={option}>{option}</option>
                        ))}
                      </select>
                    </label>

                    <div className={styles.formRow}>
                      <label className={styles.formField}>
                        <span>{resolvedForm?.location_label ?? translateText("Project Location", locale)}</span>
                        <input name="location" type="text" />
                      </label>
                      <label className={styles.formField}>
                        <span>{resolvedForm?.timeframe_label ?? translateText("Preferred Timeframe", locale)}</span>
                        <input name="timeframe" type="text" />
                      </label>
                    </div>

                    <label className={styles.formField}>
                      <span>{resolvedForm?.budget_label ?? translateText("Estimated Budget - optional", locale)}</span>
                      <input name="budget" type="text" />
                    </label>

                    <label className={styles.formField}>
                      <span>{resolvedForm?.message_label ?? translateText("Tell Us About Your Project*", locale)}</span>
                      <textarea name="message" rows={6} required />
                    </label>

                    <label className={styles.formField}>
                      <span>{resolvedForm?.referral_label ?? translateText("How Did You Hear About Us? - optional", locale)}</span>
                      <input name="referral" type="text" />
                    </label>

                    {resolvedPrivacyHref && (
                      <label className={styles.consentRow}>
                        <input type="checkbox" name="privacy-consent" required />
                        <span>
                          {resolvedForm?.consent_prefix ?? translateText("I agree to the handling of my inquiry data and accept the", locale)}
                          <a href={resolvedPrivacyHref}>
                            {` ${resolvedForm?.consent_link_label ?? translateText("Privacy Policy", locale)}`}
                          </a>.
                        </span>
                      </label>
                    )}

                    {formError && (
                      <p className={styles.formError} role="alert">
                        {formError}
                      </p>
                    )}

                    <button type="submit" className={styles.formSubmit} disabled={formSubmitting}>
                      {formSubmitting ? resolvedFormSubmittingLabel : resolvedFormSubmitLabel}
                      <span aria-hidden="true">&#8594;</span>
                    </button>
                  </form>
                </div>
              )}
            </div>
          </section>
        )}

        {!hasSections && !resolvedMinimalContent && ((resolvedFeatures?.length ?? 0) > 0 || featureParagraphs) && (
          <section className={styles.section}>
            <div className="container-fluid">
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>{resolvedFeatureTitle}</h2>
                {resolvedFeatureCopy && <p className={styles.sectionCopy}>{resolvedFeatureCopy}</p>}
              </div>

              <div className={styles.featureRail}>
                {resolvedFeatures
                  ? resolvedFeatures.map((feature, index) => (
                      <article key={`${page.slug}-feature-${index}`} className={styles.featureCard}>
                        {!resolvedHideFeatureIndex && (
                          <span className={styles.featureIndex}>
                            {String(index + 1).padStart(2, "0")}
                          </span>
                        )}
                        <div className={styles.featureDescription}>
                          <p>
                            <strong>{feature.title}</strong>
                            <br />
                            {feature.body}
                          </p>
                        </div>
                      </article>
                    ))
                  : cards.map((paragraph, index) => (
                      <article key={`${page.slug}-feature-${index}`} className={styles.featureCard}>
                        {!resolvedHideFeatureIndex && (
                          <span className={styles.featureIndex}>
                            {String(index + 1).padStart(2, "0")}
                          </span>
                        )}
                        <div
                          className={styles.featureDescription}
                          dangerouslySetInnerHTML={{ __html: paragraph.html }}
                        />
                      </article>
                    ))}
              </div>
            </div>
          </section>
        )}

        {!resolvedMinimalContent && <section className={styles.section}>
          <div className="container-fluid">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{resolvedConnectedTitle}</h2>
              {!resolvedHideConnectedIntro && (
                <p className={styles.sectionCopy}>
                  {resolvedConnectedCopy ??
                    translate("These pages should always lead cleanly into the next useful action without losing the VenueX tone.")}
                </p>
              )}
            </div>

            <div className={styles.portfolioStrip}>
              {resolvedRouteLinks.map((link) => (
                <a key={link.href} href={link.href} className={`${styles.stripCard} ${styles.routeCard}`}>
                  <span className={styles.stripLabel}>{link.meta}</span>
                  <p className={styles.stripValue}>{link.label}</p>
                </a>
              ))}
            </div>

            <div className={styles.ctaRow}>
              <a
                href={resolvedPrimaryLink.href}
                className="btn btn-rounded btn-dark-gray btn-medium btn-switch-text btn-box-shadow text-transform-none"
              >
                <span>
                  <span className="btn-double-text" data-text={resolvedPrimaryLink.label}>
                    {resolvedPrimaryLink.label}
                  </span>
                  <span>
                    <i className="fa-solid fa-arrow-right"></i>
                  </span>
                </span>
              </a>

              {resolvedSecondaryLink.label && (
                <a className={styles.secondaryLink} href={resolvedSecondaryLink.href}>
                  {resolvedSecondaryLink.label}
                </a>
              )}
            </div>
          </div>
        </section>}
      </article>
    </InnerPageChrome>
  );
}
